import React, {useMemo} from 'react';

import {DataFrame} from 'data-frames/DataFrameTypes';
import sampleRows from 'data-frames/util/sampleRows';
import {
  AnalyticsVisualizationAxisScale,
  AnalyticsVisualizationColorPalette,
  AnalyticsVisualizationLegendLocation,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import makeVisualizationConfig from 'insights/universal-blocks/makeVisualizationConfig';

import {AllDataColumnsMapping} from '../ParameterOptimizationTypes';
import ModelingDashboardBlock, {SharedModelingBlockProps} from './ModelingDashboardBlock';

export const PREDICTED_VS_ACTUAL_BLOCK_TITLE = 'Predicted vs. actual output';
const PREDICTED_VS_ACTUAL_BLOCK_DESCRIPTION =
  "The actual experimental output is plotted against the model's predicted output to give you confidence in the model's accuracy.";

/** @package */
export const MAX_PREDICTIONS_TO_SHOW = 1000;
/** @package */
export const VISUALIZATION_TYPE = VisualizationType.SCATTER_PLOT;
/** @package */
export const BASE_VISUALIZATION_PARAMETERS = {
  aggregationType: null,
  colorPalette: AnalyticsVisualizationColorPalette.DISCRETE,
  errorBarType: null,
  legendLocation: AnalyticsVisualizationLegendLocation.TOP,
  outlierConfig: null,
  regressionConfig: null,
  rowIdColumnName: null,
  seriesColumnName: null,
  xAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
  yAxisIncludesZero: false,
  yAxisMin: null,
  yAxisMax: null,
  yAxisScale: AnalyticsVisualizationAxisScale.LINEAR,
};

export interface PredictedVsActualBlockProps extends SharedModelingBlockProps {
  dataFrame: DataFrame;
  columnsMapping: AllDataColumnsMapping;
}

export default function PredictedVsActualBlock({
  dataFrame,
  columnsMapping,
  ...sharedModelingBlockProps
}: PredictedVsActualBlockProps): JSX.Element {
  const visualizationConfig = makeVisualizationConfig(VISUALIZATION_TYPE, {
    ...BASE_VISUALIZATION_PARAMETERS,
    xAxisColumnName: columnsMapping.actualOutput,
    yAxisColumnName: columnsMapping.predictedOutput,
    seriesColumnName: columnsMapping.testTrain,
  });

  const sampledDataFrame = useMemo(() => sampleRows(dataFrame, MAX_PREDICTIONS_TO_SHOW), [dataFrame]);

  return (
    <ModelingDashboardBlock
      {...sharedModelingBlockProps}
      blockTitle={PREDICTED_VS_ACTUAL_BLOCK_TITLE}
      blockDescription={PREDICTED_VS_ACTUAL_BLOCK_DESCRIPTION}
      inputDataFrame={sampledDataFrame}
      visualizationConfig={visualizationConfig}
    />
  );
}
